import { useMasterTables } from "./masterTables.queries"; // Utility functions for working with master tables data
// These replace the methods that were previously in the MasterTablesStore

const MASTER_TABLES_THERAPY_INDEX = 3;
const MASTER_TABLES_TARIFGROUP_INDEX = 10;

export const getTherapyId = (
  masterTablesData: any[] | undefined,
  tarifGroup: string | null | undefined,
  abbreviation: string | null | undefined
): string | null => {
  if (!abbreviation || !tarifGroup) return null;

  if (!masterTablesData || masterTablesData.length === 0) {
    // console.warn("Master tables data is not available when trying to get the therapy id.");
    return null;
  }

  const therapies = masterTablesData[MASTER_TABLES_THERAPY_INDEX];
  const therapy = therapies.therapy.find(
    (item: any) =>
      item.tarifGroup.id === tarifGroup &&
      item.abbreviation.toLowerCase() === abbreviation.toLowerCase()
  );
  return therapy?.id || null;
};

/**
 * MD-325: the abbreviation of a line item from its id.
 *
 * The prescription line records the practice's chosen application as an id
 * (`therapyRx.chosenTherapyId`); the Rx views need to show it as something a
 * planner recognises ("HL", "WP"). Reading it out of the master tables keeps
 * that display free — the alternative was a candidates request per line just to
 * turn one id into one word.
 */
export const getTherapyAbbreviationById = (
  masterTablesData: any[] | undefined,
  therapyId: string | null | undefined
): string | null => {
  if (!therapyId || !masterTablesData || masterTablesData.length === 0) return null;

  const therapies = masterTablesData[MASTER_TABLES_THERAPY_INDEX];
  const therapy = therapies?.therapy?.find((item: any) => item.id === therapyId);
  return therapy?.abbreviation || null;
};

export const getTarifGroupIds = (masterTablesData: any[] | undefined): string[] | null => {
  if (!masterTablesData || masterTablesData.length === 0) {
    // console.warn("Master tables data is not available when trying to get the tarif group ids.");
    return null;
  }

  const groups = masterTablesData[MASTER_TABLES_TARIFGROUP_INDEX].tarifGroup;
  return groups.map((item: any) => item.id);
};

export const getTherapyPrice = (
  masterTablesData: any[] | undefined,
  tarifGroup: string,
  abbreviation: string
): number | null => {
  if (!abbreviation || abbreviation === "") {
    throw new Error("Abbreviation is required to get the therapy price.");
  }

  if (!masterTablesData || masterTablesData.length === 0) {
    // console.warn("Master tables data is not available when trying to get the therapy price.");
    return null;
  }

  const therapies = masterTablesData[MASTER_TABLES_THERAPY_INDEX];
  const therapy = therapies.therapy.find(
    (item: any) =>
      item.tarifGroup.id === tarifGroup &&
      item.abbreviation.toLowerCase() === abbreviation.toLowerCase()
  );

  return therapy?.price || null;
};

export const getTarifGroupForRxType = (
  masterTablesData: any[] | undefined,
  rxTypeId: string
): string[] => {
  if (!masterTablesData || masterTablesData.length === 0) {
    // console.warn("Master tables data is not available when trying to get the TarifGroup.");
    return [];
  }

  const groups = masterTablesData[MASTER_TABLES_TARIFGROUP_INDEX].tarifGroup;

  const matchingGroups = groups.filter((group: any) =>
    group.rxTypes?.some((rxType: any) => rxType.id === rxTypeId)
  );

  return matchingGroups.map((group: any) => group.id);
};

// Hook facade to emulate old store shape for simpler incremental migration

export const useMasterTablesFacade = () => {
  const { data, isLoading, error } = useMasterTables();
  const safe = data || [];
  return {
    data: safe,
    isLoading,
    error,
    getTherapyId: (
      tarifGroup: string | null | undefined,
      abbreviation: string | null | undefined
    ) => getTherapyId(safe, tarifGroup, abbreviation),
    getTarifGroupIds: () => getTarifGroupIds(safe),
    getTherapyAbbreviationById: (therapyId: string | null | undefined) =>
      getTherapyAbbreviationById(safe, therapyId),
    getTherapyPrice: (tarifGroup: string, abbreviation: string) =>
      getTherapyPrice(safe, tarifGroup, abbreviation),
    getTarifGroupForRxType: (rxTypeId: string) => getTarifGroupForRxType(safe, rxTypeId),
  };
};

/**
 * Drop the groups that do not serve the practice's therapy type — so an Ergo
 * account is not offered `Blanko`/`PostBlanko`, and a Physio account is not
 * offered `ErgoBlanko`, once that group exists.
 *
 * Two deliberate escape hatches, because the catalog is filled in over time and
 * a group with no rows yet must not become unreachable:
 *  - if the filter would leave nothing, it is not applied at all;
 *  - the group the Rx already carries is always kept, so an existing Rx can
 *    still show and save its own tarif group.
 */
const narrowToTherapyType = (
  masterTablesData: any[] | undefined,
  options: string[],
  therapyType: string | null | undefined,
  currentTarifGroupId: string | null | undefined
): string[] => {
  const servedByType = getTarifGroupsForTherapyType(masterTablesData, therapyType);
  if (!servedByType || options.length <= 1) return options;

  const narrowed = options.filter(
    (id) => servedByType.includes(id) || id === currentTarifGroupId
  );
  return narrowed.length ? narrowed : options;
};

/**
 * The one rule that decides which tarif group an Rx is billed against once its
 * Rezept-typ is known. Every form that lets a user change `rxtype` calls this,
 * so the answer cannot differ between the E13 form, the Privat form and the
 * quick-add dialog.
 *
 * The catalog does NOT map one group per type — Kasse, Zahnarzt and
 * Entlassmanagement each map to GKV *and* Post, and Blankoverordnung to Blanko
 * *and* PostBlanko. Silently taking the first match there is how a Post Rx
 * became a GKV one.
 *
 * - one valid group   -> select it
 * - several valid     -> keep the current pick if it is still valid, otherwise
 *                        clear it and let the caller ask (`requiresChoice`)
 * - none mapped       -> the type is not billable through a tarif group at all
 *                        (Sonstiges, Kurverordnung, Frühförderung, Rehasport,
 *                        Functionstraining). Keep whatever is set rather than
 *                        making those types unsaveable, and offer every group.
 */
export const resolveTarifGroupForRxType = (
  masterTablesData: any[] | undefined,
  rxTypeId: string | null | undefined,
  currentTarifGroupId: string | null | undefined,
  therapyType?: string | null
): { options: string[]; nextTarifGroupId: string; requiresChoice: boolean } => {
  const options = narrowToTherapyType(
    masterTablesData,
    getTarifGroupForRxType(masterTablesData, String(rxTypeId ?? "")),
    therapyType,
    currentTarifGroupId
  );
  const current = currentTarifGroupId ?? "";

  if (options.length === 0) {
    return {
      options: getTarifGroupIds(masterTablesData) || [],
      nextTarifGroupId: current,
      requiresChoice: false,
    };
  }

  if (options.length === 1) {
    return { options, nextTarifGroupId: options[0], requiresChoice: false };
  }

  const keptCurrent = options.includes(current);
  return {
    options,
    nextTarifGroupId: keptCurrent ? current : "",
    requiresChoice: !keptCurrent,
  };
};

/**
 * The Rezept-typ every Blanko tarif group hangs off. There is no separate
 * "is this a Blanko group" flag — the catalog already answers it, so this is
 * the only literal needed to derive the whole set.
 */
export const BLANKOVERORDNUNG_RX_TYPE_ID = "Blankoverordnung";

/**
 * Every tarif group billed as a Blankoverordnung — `Blanko` and `PostBlanko`
 * today, plus `ErgoBlanko` the day it is seeded, with no code change here.
 *
 * Read from the catalog rather than kept as a hand-maintained list: the two
 * places that hardcoded the pair each drifted (the patient form never learnt
 * about PostBlanko, and the Blanko Rx form rewrote every Rx it opened to
 * `Blanko`).
 */
export const getBlankoTarifGroupIds = (masterTablesData: any[] | undefined): string[] =>
  getTarifGroupForRxType(masterTablesData, BLANKOVERORDNUNG_RX_TYPE_ID);

export const isBlankoTarifGroup = (
  masterTablesData: any[] | undefined,
  tarifGroupId: string | null | undefined
): boolean => !!tarifGroupId && getBlankoTarifGroupIds(masterTablesData).includes(tarifGroupId);

/**
 * Tarif groups that carry at least one line item of the given therapy type.
 *
 * `TarifGroup` has no therapyType of its own; a `Therapy` row is keyed by
 * (method, tarifGroup, therapyType), so the catalog is what says whether a
 * group serves Physio, Ergo or — like GKV — both.
 *
 * Returns null when the question cannot be answered (no data, no type asked),
 * which callers read as "do not filter".
 */
export const getTarifGroupsForTherapyType = (
  masterTablesData: any[] | undefined,
  therapyType: string | null | undefined
): string[] | null => {
  if (!therapyType || !masterTablesData || masterTablesData.length === 0) return null;

  const therapies = masterTablesData[MASTER_TABLES_THERAPY_INDEX]?.therapy;
  if (!therapies?.length) return null;

  const served = new Set<string>();
  for (const item of therapies) {
    // A row without a therapyType serves every type — the same convention the
    // forms already use when filtering their Heilmittel lists.
    if (item?.tarifGroup?.id && (!item.therapyType || item.therapyType === therapyType)) {
      served.add(item.tarifGroup.id);
    }
  }
  return served.size ? Array.from(served) : null;
};
